import { FC, useState } from 'react';
import clsx from 'clsx';
import { useBoxTimeline } from '../hooks/useBoxTimeline';
import { TimelineEvent } from '../types/sensors';
import { ALARM_LABEL, severityColor } from '../../../components/box-status-card/severity';

type Props = {
  box_id: number;
  cmb_number: string;
  company_name: string | null;
  project_name: string | null;
  onClose: () => void;
};

const EVENT_GROUPS: Record<string, string[]> = {
  alarms: ['alarm_triggered', 'alarm_resolved'],
  commands: ['command_sent', 'command_response'],
  heartbeats: ['heartbeat'],
};

export const BoxTimelineDrawer: FC<Props> = ({ box_id, cmb_number, company_name, project_name, onClose }) => {
  const [filter, setFilter] = useState<'all' | 'alarms' | 'commands' | 'heartbeats'>('all');
  const [periodHours, setPeriodHours] = useState(24);

  const event_types = filter === 'all'
    ? [...EVENT_GROUPS.alarms, ...EVENT_GROUPS.commands]
    : EVENT_GROUPS[filter];

  const since = new Date(Date.now() - periodHours * 3_600_000).toISOString();

  const { data, isFetching } = useBoxTimeline(box_id, {
    page: 1,
    items_per_page: 50,
    event_types,
    since,
  });

  const events: TimelineEvent[] = data?.data ?? [];

  return (
    <div
      style={{
        position: 'fixed', right: 0, top: 0, bottom: 0, width: 380,
        background: '#fff', borderLeft: '1px solid #e5e7eb',
        boxShadow: '-4px 0 8px rgba(0,0,0,0.04)', zIndex: 1050,
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div className="p-3 border-bottom d-flex justify-content-between align-items-start">
        <div>
          <div className="fw-bold">CMB {cmb_number}</div>
          <div className="text-muted fs-8">{[company_name, project_name].filter(Boolean).join(' · ')}</div>
        </div>
        <button type="button" className="btn btn-sm btn-icon" onClick={onClose}>✕</button>
      </div>
      <div className="p-3 border-bottom">
        <div className="d-flex" style={{ gap: 4 }}>
          {(['all', 'alarms', 'commands', 'heartbeats'] as const).map((f) => (
            <span
              key={f}
              role="button"
              onClick={() => setFilter(f)}
              className={clsx('px-2 py-1 rounded-pill fs-8', filter === f ? 'bg-primary text-white' : 'bg-light')}
              style={{ cursor: 'pointer' }}
            >
              {f === 'all' ? 'Todos' : f === 'alarms' ? 'Alarmes' : f === 'commands' ? 'Comandos' : 'Heartbeats'}
            </span>
          ))}
        </div>
        <select
          className="form-select form-select-sm mt-2"
          value={periodHours}
          onChange={(e) => setPeriodHours(+e.target.value)}
        >
          <option value={1}>Última hora</option>
          <option value={24}>Últimas 24h</option>
          <option value={168}>Última semana</option>
        </select>
      </div>
      <div className="flex-grow-1 p-3" style={{ overflowY: 'auto' }}>
        {isFetching && events.length === 0 && <div className="text-muted">Carregando...</div>}
        {!isFetching && events.length === 0 && (
          <div className="text-muted text-center py-5">Sem eventos no período.</div>
        )}
        {events.map((e) => {
          const colors = e.severity ? severityColor(e.severity) : { bg: '#f1f5f9', text: '#374151', bar: '#94a3b8' };
          const label = e.type === 'alarm_triggered'
            ? `${e.alarm_type ? ALARM_LABEL[e.alarm_type] : 'Alarme'} disparado`
            : e.type === 'alarm_resolved'
            ? `${e.alarm_type ? ALARM_LABEL[e.alarm_type] : 'Alarme'} resolvido`
            : e.type === 'command_sent'
            ? `Comando ${e.command_type_id ? `#${e.command_type_id}` : ''} enviado${e.by_user_name ? ' por ' + e.by_user_name : ''}`
            : e.type === 'command_response'
            ? `Resposta: ${e.ok ? 'ok' : 'falha'}`
            : 'Heartbeat';
          return (
            <div key={`${e.type}-${e.id}-${e.at}`} className="d-flex mb-2" style={{ gap: 8, fontSize: 12 }}>
              <span className="text-muted" style={{ width: 50 }}>
                {new Date(e.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span style={{ color: colors.bar }}>●</span>
              <span style={{ flex: 1 }}>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
